import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GraduationCap, Plus, Pencil, Trash2, ArrowRight, Briefcase, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: 'applied', label: 'Applied', color: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200' },
  { key: 'screening', label: 'Screening', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  { key: 'interview', label: 'Interview', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200' },
  { key: 'selected', label: 'Selected', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  { key: 'onboarded', label: 'Onboarded', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200' },
  { key: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  { key: 'completed', label: 'Completed', color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200' },
  { key: 'converted', label: 'Converted to FTE', color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' },
  { key: 'dropped', label: 'Dropped', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
];

const stageBadge = (stage) => {
  const s = STAGES.find(x => x.key === stage);
  return <Badge variant="secondary" className={s?.color}>{s?.label || stage}</Badge>;
};

const EMPTY_FORM = {
  name: '', email: '', phone: '', university: '', degree: '', graduation_year: '',
  tech_stack: '', mentor_name: '', mentor_id: '', project: '', stipend: '',
  start_date: '', end_date: '', duration_months: '',
  stage: 'applied', evaluation_score: '', feedback: '', fte_recommendation: '', notes: '',
};

const InternshipsPage = () => {
  const [interns, setInterns] = useState([]);
  const [stats, setStats] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntern, setEditingIntern] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Convert-to-FTE dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertIntern, setConvertIntern] = useState(null);
  const [convertForm, setConvertForm] = useState({ department: '', designation: '', date_of_joining: new Date().toISOString().slice(0, 10), ctc: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = stageFilter !== 'all' ? `?stage=${stageFilter}` : '';
      const [list, st] = await Promise.all([
        axios.get(`${API_URL}/api/internships${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/internships/stats`, { withCredentials: true }),
      ]);
      setInterns(list.data);
      setStats(st.data);
    } catch (err) {
      console.error('Fetch internships error:', err);
      toast.error('Failed to load internships');
    } finally {
      setLoading(false);
    }
  }, [stageFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingIntern(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (intern) => {
    setEditingIntern(intern);
    setForm({
      ...EMPTY_FORM,
      ...intern,
      tech_stack: Array.isArray(intern.tech_stack) ? intern.tech_stack.join(', ') : (intern.tech_stack || ''),
      graduation_year: intern.graduation_year || '',
      stipend: intern.stipend || '',
      evaluation_score: intern.evaluation_score || '',
      duration_months: intern.duration_months || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    const payload = {
      ...form,
      tech_stack: typeof form.tech_stack === 'string'
        ? form.tech_stack.split(',').map(s => s.trim()).filter(Boolean)
        : form.tech_stack,
      stipend: parseFloat(form.stipend) || 0,
      graduation_year: form.graduation_year ? parseInt(form.graduation_year) : null,
      duration_months: form.duration_months ? parseInt(form.duration_months) : null,
      evaluation_score: form.evaluation_score ? parseFloat(form.evaluation_score) : null,
    };
    try {
      if (editingIntern) {
        await axios.put(`${API_URL}/api/internships/${editingIntern.id}`, payload, { withCredentials: true });
        toast.success('Internship updated');
      } else {
        await axios.post(`${API_URL}/api/internships`, payload, { withCredentials: true });
        toast.success('Internship added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleArchive = async (intern) => {
    if (!window.confirm(`Archive ${intern.name}? This will remove them from active lists.`)) return;
    try {
      await axios.delete(`${API_URL}/api/internships/${intern.id}`, { withCredentials: true });
      toast.success('Intern archived');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    }
  };

  const openConvert = (intern) => {
    setConvertIntern(intern);
    setConvertForm({ department: '', designation: '', date_of_joining: new Date().toISOString().slice(0, 10), ctc: '' });
    setConvertOpen(true);
  };

  const handleConvert = async () => {
    if (!convertForm.department || !convertForm.designation) {
      toast.error('Department and designation are required');
      return;
    }
    try {
      const payload = {
        ...convertForm,
        ctc: convertForm.ctc ? parseFloat(convertForm.ctc) : null,
      };
      const res = await axios.post(
        `${API_URL}/api/internships/${convertIntern.id}/convert-to-fte`,
        payload,
        { withCredentials: true },
      );
      const msg = res.data.temp_password
        ? `${res.data.message} | Temp password: ${res.data.temp_password}`
        : (res.data.message || 'Converted to FTE');
      toast.success(msg, { duration: 15000 });
      setConvertOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="internships-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-primary" />
            Internship Management
          </h1>
          <p className="text-muted-foreground">Pipeline for identifying and nurturing talent before full-time hiring</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="new-intern-btn">
          <Plus className="w-4 h-4" /> Add Intern
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="intern-stats">
          <StatCard icon={<Users />} label="Total" value={stats.total} />
          <StatCard icon={<Briefcase />} label="In Pipeline" value={stats.in_pipeline} accent="indigo" />
          <StatCard icon={<TrendingUp />} label="Active" value={stats.active} accent="emerald" />
          <StatCard icon={<ArrowRight />} label="Converted" value={stats.converted} accent="green" />
          <StatCard label="Dropped" value={stats.dropped} accent="rose" />
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-base">All Interns</CardTitle>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-[200px]" data-testid="stage-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-muted-foreground">Loading...</p>
          ) : interns.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No interns found. Add your first intern to start tracking.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="data-table w-full text-sm" data-testid="interns-table">
                <thead>
                  <tr>
                    <th>Name</th><th>University</th><th>Stage</th><th>Mentor</th>
                    <th>Project</th><th>Duration</th><th>Stipend</th><th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {interns.map(intern => (
                    <tr key={intern.id} data-testid={`intern-row-${intern.id}`}>
                      <td>
                        <div className="font-medium">{intern.name}</div>
                        <div className="text-xs text-muted-foreground">{intern.email}</div>
                      </td>
                      <td>
                        <div className="text-sm">{intern.university || '—'}</div>
                        <div className="text-xs text-muted-foreground">{intern.degree}{intern.graduation_year ? ` · ${intern.graduation_year}` : ''}</div>
                      </td>
                      <td>{stageBadge(intern.stage)}</td>
                      <td className="text-sm">{intern.mentor_name || '—'}</td>
                      <td className="text-sm max-w-[200px] truncate" title={intern.project}>{intern.project || '—'}</td>
                      <td className="text-sm">
                        {intern.start_date && intern.end_date
                          ? `${intern.start_date} → ${intern.end_date}`
                          : (intern.duration_months ? `${intern.duration_months} mo` : '—')}
                      </td>
                      <td className="text-sm font-mono">{intern.stipend ? `₹${(intern.stipend).toLocaleString('en-IN')}` : '—'}</td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          {(intern.stage === 'completed' || intern.stage === 'active') && !intern.converted_to_employee_id && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                                    title="Convert to FTE" onClick={() => openConvert(intern)}
                                    data-testid={`convert-fte-${intern.id}`}>
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit"
                                  onClick={() => openEdit(intern)} data-testid={`edit-intern-${intern.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700"
                                  title="Archive" onClick={() => handleArchive(intern)}
                                  data-testid={`archive-intern-${intern.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingIntern ? 'Edit Intern' : 'Add New Intern'}</DialogTitle>
            <DialogDescription>
              {editingIntern ? 'Update the intern\'s details, evaluation, and stage.' : 'Capture an applicant\'s basic info, education, program details, and evaluation criteria.'}
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basic" className="mt-2">
            <TabsList>
              <TabsTrigger value="basic" data-testid="tab-basic">Basic Info</TabsTrigger>
              <TabsTrigger value="education">Education</TabsTrigger>
              <TabsTrigger value="program" data-testid="tab-program">Program</TabsTrigger>
              <TabsTrigger value="eval">Evaluation</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-3 mt-4">
              <Row>
                <Field label="Name *"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="intern-name" /></Field>
                <Field label="Email *"><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="intern-email" /></Field>
              </Row>
              <Row>
                <Field label="Phone"><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="Stage">
                  <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                    <SelectTrigger data-testid="intern-stage"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </Row>
            </TabsContent>
            <TabsContent value="education" className="space-y-3 mt-4">
              <Row>
                <Field label="University / College"><Input value={form.university} onChange={e => setForm({ ...form, university: e.target.value })} /></Field>
                <Field label="Degree"><Input value={form.degree} onChange={e => setForm({ ...form, degree: e.target.value })} placeholder="B.Tech CS / M.Tech AI" /></Field>
              </Row>
              <Row>
                <Field label="Graduation Year"><Input type="number" value={form.graduation_year} onChange={e => setForm({ ...form, graduation_year: e.target.value })} /></Field>
                <Field label="Tech Stack (comma-separated)"><Input value={form.tech_stack} onChange={e => setForm({ ...form, tech_stack: e.target.value })} placeholder="Python, React, MongoDB" /></Field>
              </Row>
            </TabsContent>
            <TabsContent value="program" className="space-y-3 mt-4">
              <Row>
                <Field label="Mentor Name"><Input value={form.mentor_name} onChange={e => setForm({ ...form, mentor_name: e.target.value })} /></Field>
                <Field label="Project"><Input value={form.project} onChange={e => setForm({ ...form, project: e.target.value })} placeholder="What will they work on?" /></Field>
              </Row>
              <Row>
                <Field label="Start Date"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
                <Field label="End Date"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
              </Row>
              <Row>
                <Field label="Duration (months)"><Input type="number" value={form.duration_months} onChange={e => setForm({ ...form, duration_months: e.target.value })} /></Field>
                <Field label="Monthly Stipend (₹)"><Input type="number" value={form.stipend} onChange={e => setForm({ ...form, stipend: e.target.value })} /></Field>
              </Row>
            </TabsContent>
            <TabsContent value="eval" className="space-y-3 mt-4">
              <Row>
                <Field label="Evaluation Score (0-100)"><Input type="number" min="0" max="100" value={form.evaluation_score} onChange={e => setForm({ ...form, evaluation_score: e.target.value })} /></Field>
                <Field label="FTE Recommendation">
                  <Select value={form.fte_recommendation || 'none'} onValueChange={v => setForm({ ...form, fte_recommendation: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not yet —</SelectItem>
                      <SelectItem value="yes">✅ Recommend Hire</SelectItem>
                      <SelectItem value="maybe">🟡 Maybe / Re-evaluate</SelectItem>
                      <SelectItem value="no">❌ Do Not Hire</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </Row>
              <Field label="Mentor Feedback"><Textarea rows={3} value={form.feedback} onChange={e => setForm({ ...form, feedback: e.target.value })} /></Field>
              <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-intern">{editingIntern ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to FTE Dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert {convertIntern?.name} to Full-Time Employee</DialogTitle>
            <DialogDescription>
              A new Employee record + login account + a placeholder salary structure will be created. HR can fine-tune comp later from the Payroll module.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Field label="Department *"><Input value={convertForm.department} onChange={e => setConvertForm({ ...convertForm, department: e.target.value })} placeholder="Engineering" data-testid="convert-dept" /></Field>
            <Field label="Designation *"><Input value={convertForm.designation} onChange={e => setConvertForm({ ...convertForm, designation: e.target.value })} placeholder="Software Engineer" data-testid="convert-desig" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of Joining"><Input type="date" value={convertForm.date_of_joining} onChange={e => setConvertForm({ ...convertForm, date_of_joining: e.target.value })} /></Field>
              <Field label="Annual CTC (₹)">
                <Input type="number" value={convertForm.ctc} onChange={e => setConvertForm({ ...convertForm, ctc: e.target.value })} placeholder="e.g. 800000" data-testid="convert-ctc" />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">A login account will be auto-created — the temporary password will be shown on success so you can share it securely.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button>
            <Button onClick={handleConvert} className="gap-2" data-testid="confirm-convert">
              <ArrowRight className="w-4 h-4" /> Convert to FTE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon, label, value, accent }) => {
  const palette = {
    indigo: 'text-indigo-600 dark:text-indigo-300',
    emerald: 'text-emerald-600 dark:text-emerald-300',
    green: 'text-green-600 dark:text-green-300',
    rose: 'text-rose-600 dark:text-rose-300',
  };
  const cls = palette[accent] || 'text-primary';
  return (
    <div className="p-3 rounded-md border bg-muted/30">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon && <span className="w-4 h-4">{icon}</span>}
        <span>{label}</span>
      </div>
      <p className={`text-2xl font-bold mt-1 ${cls}`}>{value}</p>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

const Row = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
);

export default InternshipsPage;
